import { Injectable, computed, effect, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { QueryClient } from '@tanstack/angular-query-experimental';

import {
  WidgetViewPreset,
  WidgetViewPresetScope,
  injectSaveViewPresetMutation,
  injectWidgetViewPresetQuery,
  viewPresetQueryKey,
  widgetViewPresetContext,
} from 'src/app/api/queries/view-preset.queries';
import { AppState } from 'src/app/reducers';

import { changeWidgetsLanaguage } from '../actions/feed.actions';
import { NomObjectClass } from '../constants';
import { selectCurrentObject, selectUser } from '../reducers';
import { PropertyViewConfig } from './feed-settings.service';

/**
 * The current object's `pv-widgets` view preset - the widget language selection
 * and the feed's compact-widget-list edit mode.
 *
 * **A root service, and deliberately the only observer**, because the blob follows
 * the *current object* rather than any one component: the language dropdown, three
 * views' edit mode and the derived language id all read it.
 *
 * **It must be alive from startup**, because the `changeWidgetsLanaguage`
 * derivation below is load-bearing for consumers that never touch this service
 * (Presentation Mode reads `widgetsLanguageId` and has no widget toolbar). Root
 * providers are lazy, so `CoreEffects` injects it to force construction.
 */
@Injectable({ providedIn: 'root' })
export class WidgetViewPresetService {
  private readonly store = inject(Store<AppState>);
  private readonly queryClient = inject(QueryClient);
  private readonly saveMutation = injectSaveViewPresetMutation();

  private readonly currentObject = this.store.selectSignal(selectCurrentObject);
  private readonly user = this.store.selectSignal(selectUser);

  /** No object, no fetch. Workspaces have no widgets, so no `pv-widgets` blob. */
  private readonly scope = computed<WidgetViewPresetScope | null>(() => {
    const object = this.currentObject();
    const user = this.user();

    return object && user && object.object_class_id !== NomObjectClass.WORKSPACE
      ? {
          objectId: object.id,
          objectClassId: object.object_class_id,
          user,
        }
      : null;
  });

  private readonly query = injectWidgetViewPresetQuery(this.scope);

  /** The language options, defaults merged. */
  readonly languageConfig = computed<PropertyViewConfig[]>(
    () => this.query.data()?.language ?? [],
  );

  /** The feed's compact widget list. */
  readonly editMode = computed<boolean>(
    () => this.query.data()?.editMode ?? false,
  );

  constructor() {
    // * `FeedState.widgetsLanguageId` stays NgRx: cross-component client state with
    // * many readers, and a term in the widget-data query key. This derivation is
    // * its only writer.
    effect(() => {
      const selected = this.languageConfig().find((item) => item.selected);
      if (!selected) return;

      this.store.dispatch(
        changeWidgetsLanaguage({ languageId: selected.path as number }),
      );
    });
  }

  /** Pick a language by its label. */
  selectLanguage(optionName: string): void {
    this.patch((preset) => ({
      ...preset,
      language: preset.language.map((item) => ({
        ...item,
        selected: item.label === optionName,
      })),
    }));
  }

  /**
   * Flip the feed's compact widget list and return the new value. The return
   * matters: `feed.component.widgetConfigure()` acts on the flipped state in the
   * same tick, which a `setQueryData` patch does not provide - TanStack notifies
   * its observers a microtask later.
   */
  toggleEditMode(): boolean {
    let next = this.editMode();

    this.patch((preset) => {
      next = !preset.editMode;
      return { ...preset, editMode: next };
    });

    return next;
  }

  /**
   * Patch the cached blob and persist it.
   *
   * **`editMode` is omitted for non-feeds**: a dashboard has no compact widget
   * view, and a stored `true` there used to disable every widget refresh. The read
   * side treats an absent key as `false`.
   */
  private patch(next: (preset: WidgetViewPreset) => WidgetViewPreset): void {
    const scope = this.scope();
    if (!scope) return;

    const context = widgetViewPresetContext(scope);
    const queryKey = viewPresetQueryKey(context);

    const current = this.queryClient.getQueryData<WidgetViewPreset>(queryKey);
    if (!current) return;

    const preset = next(current);
    this.queryClient.setQueryData(queryKey, preset);

    const isFeed = scope.objectClassId === NomObjectClass.FEED;

    this.saveMutation.mutate({
      context,
      settings: {
        language: preset.language,
        ...(isFeed ? { editMode: preset.editMode } : {}),
      },
    });
  }
}
